import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Copy, Download, Search, BadgeCheck, Link2, TrendingUp } from "lucide-react";
import { toast, Toaster } from "sonner";
import PublicLayout from "../components/PublicLayout";
import SEO from "../components/SEO";
import { apiClient } from "../lib/api";

const SITE = process.env.REACT_APP_SITE_URL.replace(/\/$/, "");
const norm = (s) => s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

const snippet = (theme, city) => {
  const href = city ? `${SITE}/villes/${city.slug}` : `${SITE}/`;
  const title = city ? `Conciergerie Airbnb à ${city.name}` : "Comparateur de conciergerie Airbnb en Bretagne";
  const alt = city ? `Conciergerie partenaire de Bretagne Conciergerie Comparateur à ${city.name}` : "Conciergerie partenaire de Bretagne Conciergerie Comparateur";
  return `<a href="${href}" title="${title}" target="_blank"><img src="${SITE}/badges/partenaire-${theme}.png" alt="${alt}" width="284" height="58" loading="lazy" style="max-width:100%;height:auto;border:0"></a>`;
};

const CityPicker = ({ cities, city, setCity }) => {
  const [q, setQ] = useState("");
  const match = useMemo(() => (q.length < 2 ? [] : cities.filter((c) => norm(c.name).startsWith(norm(q))).slice(0, 6)), [q, cities]);
  return (
    <div className="relative">
      <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
      <input value={city ? city.name : q} onChange={(e) => { setCity(null); setQ(e.target.value); }} placeholder="Votre commune principale (ex : Vannes)"
        className="w-full pl-11 pr-4 py-3 rounded-full border border-border bg-white focus:outline-none focus:border-accent" data-testid="badge-city-input" />
      {match.length > 0 && !city && (
        <div className="absolute left-0 right-0 top-full mt-2 surface-card p-1.5 z-20" data-testid="badge-city-suggestions">
          {match.map((c) => (
            <button key={c.slug} onClick={() => { setCity(c); setQ(""); }} className="w-full text-left px-3 py-2 rounded-lg hover:bg-secondary text-sm flex justify-between" data-testid={`badge-suggest-${c.slug}`}>
              <span className="font-medium">{c.name}</span><span className="text-muted-foreground text-xs">{c.department}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const ThemeToggle = ({ theme, setTheme }) => (
  <div className="inline-flex p-1 rounded-full bg-secondary" data-testid="badge-theme-toggle">
    {[["light", "Pour fond clair"], ["dark", "Pour fond sombre"]].map(([v, l]) => (
      <button key={v} onClick={() => setTheme(v)} data-testid={`badge-theme-${v}`}
        className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${theme === v ? "bg-white shadow text-primary" : "text-muted-foreground"}`}>{l}</button>
    ))}
  </div>
);

const STEPS = [
  { icon: BadgeCheck, t: "Choisissez votre badge", d: "Fond clair ou sombre, selon votre site." },
  { icon: Link2, t: "Indiquez votre commune", d: "Le lien mène vers la page de votre secteur." },
  { icon: TrendingUp, t: "Collez le code", d: "Dans le pied de page ou la page « À propos » de votre site." },
];

export default function PartnerBadgePage() {
  const [cities, setCities] = useState([]);
  const [city, setCity] = useState(null);
  const [theme, setTheme] = useState("light");
  const [copied, setCopied] = useState(false);
  useEffect(() => { apiClient.get("/cities").then((r) => setCities(r.data)).catch(() => {}); }, []);
  const code = snippet(theme, city);

  const copy = async () => {
    await navigator.clipboard.writeText(code);
    setCopied(true);
    toast.success("Code copié, il ne reste qu'à le coller sur votre site");
    setTimeout(() => setCopied(false), 2500);
  };

  return (
    <PublicLayout>
      <Toaster position="top-center" richColors />
      <SEO title="Badge conciergerie partenaire" description="Conciergeries partenaires : affichez le badge Bretagne Conciergerie Comparateur sur votre site en 1 minute et rassurez vos propriétaires." />
      <section className="pt-8 pb-12" data-testid="badge-hero">
        <div className="container-tm max-w-4xl">
          <span className="pill">Espace partenaires</span>
          <h1 className="h-display mt-5">Affichez votre <span className="text-accent">badge partenaire</span></h1>
          <p className="mt-5 text-lg text-muted-foreground">Vous faites partie de notre réseau de conciergeries ? Montrez-le à vos propriétaires : le badge rassure vos visiteurs et renforce la visibilité de votre secteur sur Google.</p>
          <div className="mt-8 grid sm:grid-cols-3 gap-4">
            {STEPS.map((s, i) => (
              <div key={s.t} className="surface-card p-5" data-testid={`badge-step-${i}`}>
                <s.icon size={20} className="text-accent" />
                <div className="mt-3 font-bold">{i + 1}. {s.t}</div>
                <div className="text-sm text-muted-foreground mt-1">{s.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section className="pb-20" data-testid="badge-generator">
        <div className="container-tm max-w-4xl surface-card p-6 md:p-10">
          <div className="flex flex-col md:flex-row gap-4 md:items-center md:justify-between">
            <ThemeToggle theme={theme} setTheme={setTheme} />
            <div className="md:w-80"><CityPicker cities={cities} city={city} setCity={setCity} /></div>
          </div>
          <div className={`mt-8 rounded-3xl p-8 md:p-12 flex justify-center ${theme === "dark" ? "bg-[#0b1426]" : "bg-secondary"}`} data-testid="badge-preview">
            <img src={`/badges/partenaire-${theme}.png`} alt="Aperçu du badge partenaire" width="284" height="58" className="max-w-full h-auto" />
          </div>
          <label className="block mt-8 text-sm font-semibold">Code à copier sur votre site</label>
          <textarea readOnly value={code} rows={4} onFocus={(e) => e.target.select()}
            className="mt-2 w-full rounded-2xl border border-border bg-secondary/50 p-4 font-mono text-xs leading-relaxed" data-testid="badge-code" />
          <div className="mt-4 flex flex-wrap items-center gap-4">
            <button onClick={copy} className="btn-accent" data-testid="badge-copy-btn">{copied ? <Check size={16} /> : <Copy size={16} />} {copied ? "Copié !" : "Copier le code"}</button>
            <a href={`/badges/partenaire-${theme}.png`} download className="btn-outline inline-flex items-center gap-2 px-5 py-3 rounded-full border border-border font-semibold text-sm hover:border-accent transition-colors" data-testid="badge-download-btn"><Download size={16} /> Télécharger l'image (PNG transparent)</a>
            <span className="text-sm text-muted-foreground">Pas encore partenaire ? <Link to="/devenir-partenaire" className="text-accent font-semibold hover:underline" data-testid="badge-become-partner">Rejoindre le réseau</Link></span>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
